import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import ReactSelect from "react-select";
import { useAtom } from "jotai";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaReceipt,
  FaLock,
  FaLockOpen,
  FaFilter,
  FaCalendarAlt,
} from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import { confirmAlert } from "react-confirm-alert";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { DatePicker, Space } from "antd";
import Modal from "../../../components/Modal/Modal";
import ErrorBoundaryRoutes from "../../../components/shared/ErrorBoundaryRoutes";
import { gbState } from "../../../components/shared/GlobalState";
import ViewModal from "../View/ViewModal";

const ListIncomeDuesChild = () => {
  dayjs.extend(utc);
  let navigate = useNavigate();
  //Error Logging Service
  const [{ errorLogApi, user }] = useAtom(gbState);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [financial_year] = useState({
    value: user?.current_financial_year,
    label: user?.current_financial_year,
  });
  const [showModal, setShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [memberFilter, setMemberFilter] = useState("all");
  const [showFilters, setShowFilters] = useState(true);

  // Month filter
  const [monthFilter, setMonthFilter] = useState(null);

  const toggleModal = () => {
    setShowModal(!showModal);
  };

  const handleDelete = (id) => {
    confirmAlert({
      title: "",
      message: "Are you sure to delete this Receipt ?",
      buttons: [
        {
          label: "Yes",
          onClick: () => deleteRecord(id),
        },
        {
          label: "No",
          //onClick: () => alert('Click No')
        },
      ],
    });
  };

  const deleteRecord = (id) => {
    let userToken = localStorage.getItem("token");
    const config = {
      headers: { Authorization: `Bearer ${userToken}` },
    };
    axios
      .delete(
        `${process.env.REACT_APP_API_URL}/incomedues/delete/${id}`,
        config
      )
      .then((response) => {
        // alert(JSON.stringify(response));
        getDataFromServer();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message);
        errorLogApi(err, "ListIncomeDuesChild", "/incomedues/delete");
      });
  };

  const getDataFromServer = () => {
    let userToken = localStorage.getItem("token");

    let urlLink = `${process.env.REACT_APP_API_URL}/incomedues/${user?.church_id}`;

    const config = {
      method: "GET",
      url: urlLink,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };
    axios(config)
      .then(function (response) {
        //console.log(JSON.stringify(response.data.users));
        const oldData = response?.data?.filter(
          (finFilter) => finFilter?.financial_year === financial_year?.label
        );
        const updatedData = oldData.map((data) => {
          return {
            ...data,
            id: data?._id,
            category: data?.category_id?.category_name,
            description: data?.description,
            start_date: dayjs.utc(data?.start_date),
            pledged_amount: data?.pledged_amount,
            balance_amount: data?.balance_amount,
            dues_title: data?.dues_title,
            record_close_status: data?.record_close_status,
            member_name: data?.member_id?.first_name
              ? data?.member_id?.first_name + " " + data?.member_id?.last_name
              : " ",
          };
        });

        console.log(updatedData);
        setData(updatedData);
      })
      .catch(function (err) {
        console.log("error api call ", err);
      });
  };

  useEffect(() => {
    getDataFromServer();
  }, [financial_year]);

  const getTotalPayment = (id) => {
    let userToken = localStorage.getItem("token");
    let urlLink = `${process.env.REACT_APP_API_URL}/incomedues/get-total-paid/${id}`;
    const config = {
      method: "GET",
      url: urlLink,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };
    axios(config)
      .then(function (response) {
        console.log(response.data);
        return response.data;
      })
      .catch(function (err) {
        console.log("error api call ", err);
      });
  };

  const paymentStatusOption = ["Pending", "Settled", "Declined"];
  const statusTxtColor = ["text-white", "text-gray-800", "text-white"];
  const statusBgColor = ["bg-orange-400", "bg-green-400", "bg-red-400"];

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      // 🔍 1. Search filter
      const matchesSearch = Object.values(item).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      );

      // 🕒 2. Force all dates into UTC mode
      const itemDate = dayjs.utc(item.start_date);
      const start = startDate ? dayjs.utc(startDate) : null;
      const end = endDate ? dayjs.utc(endDate) : null;

      // 📅 3. Date range filter (UTC-safe)
      const matchesDateRange =
        (!start ||
          itemDate.isAfter(start, "day") ||
          itemDate.isSame(start, "day")) &&
        (!end || itemDate.isBefore(end, "day") || itemDate.isSame(end, "day"));

      // 🏷️ 4. Status, Category, and Member filters
      const matchesStatus =
        statusFilter === "all" || item.status === statusFilter;

      const matchesCategory =
        categoryFilter === "all" || item.category === categoryFilter;

      const matchesMember =
        memberFilter === "all" || item.member_name === memberFilter;

      // 🗓️ 5. Month-Year filter (UTC-safe)
      const matchesMonth = monthFilter?.value
        ? (() => {
            const selectedMonth = parseInt(monthFilter.value); // 1–12
            const selectedYear = monthFilter.year;

            const itemMonth = itemDate.month() + 1; // dayjs months are 0-based
            const itemYear = itemDate.year();

            return itemMonth === selectedMonth && itemYear === selectedYear;
          })()
        : true;

      // ✅ 6. Return combined result
      return (
        matchesSearch &&
        matchesDateRange &&
        matchesStatus &&
        matchesCategory &&
        matchesMember &&
        matchesMonth
      );
    });
  }, [
    data,
    searchTerm,
    startDate,
    endDate,
    statusFilter,
    categoryFilter,
    memberFilter,
    monthFilter,
  ]);

  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(data.map((item) => item.category))];
    return uniqueCategories;
  }, [data]);

  const members = useMemo(() => {
    // Create member array from data
    const memberArray = data.map((item) => item.member_name);
    // Get unique members
    const uniqueMembers = [...new Set(memberArray)];
    // Filter out empty or whitespace-only names
    return uniqueMembers
      .filter((member) => member && member.trim() !== "")
      .sort();
  }, [data]);

  // Month options for the filter
  const monthOptions = useMemo(() => {
    const months = [];
    const currentYear = dayjs().year();

    // Generate options for current year and previous year
    for (let year = currentYear - 1; year <= currentYear; year++) {
      for (let month = 1; month <= 12; month++) {
        const monthStr = month.toString().padStart(2, "0");
        const monthLabel = dayjs(`${year}-${monthStr}-01`).format("MMMM YYYY");
        months.push({
          value: monthStr,
          label: monthLabel,
          year: year,
        });
      }
    }

    // Sort by year and month (newest first)
    return months.sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.value - a.value;
    });
  }, []);

  const clearFilters = () => {
    setStartDate(null);
    setEndDate(null);
    setStatusFilter("all");
    setCategoryFilter("all");
    setMemberFilter("all");
    setSearchTerm("");
    setMonthFilter(null);
  };

  const renderTableCell = (row) => (
    <>
      <TableCell>
        <div className="flex items-center space-x-2">
          <Tooltip title="View details">
            <IconButton
              size="small"
              onClick={() => {
                toggleModal();
                setCurrentRecord(row);
              }}
              className="hover:bg-blue-50"
            >
              <FaEye className="text-blue-600" />
            </IconButton>
          </Tooltip>
          {user?.is_a_trustee === true && (
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => {
                  if (row.record_close_status) {
                    toast.error("Record is already closed.");
                  } else {
                    navigate("/dues-income/update", { state: row });
                  }
                }}
                className="hover:bg-gray-100"
              >
                <FaEdit className="text-gray-600" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Generate Receipt">
            <IconButton
              size="small"
              onClick={() => {
                if (row.record_close_status) {
                  toast.error("Record is already closed.");
                } else {
                  navigate("/dues-income/receipt", { state: row });
                }
              }}
              className="hover:bg-green-50"
            >
              <FaReceipt className="text-green-600" />
            </IconButton>
          </Tooltip>
          {user?.is_a_trustee === true && (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => {
                  if (row.record_close_status) {
                    toast.error(
                      "Cannot be deleted! Monthly record is already closed for this month."
                    );
                  } else {
                    handleDelete(row.id);
                  }
                }}
                className="hover:bg-red-50"
              >
                <FaTrash className="text-red-600" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </TableCell>
      {!isMobile && (
        <TableCell>
          {dayjs(row.start_date).utc().format("MM/DD/YYYY")}
        </TableCell>
      )}
      <TableCell>{row.member_name}</TableCell>
      {!isTablet && <TableCell>{row.category}</TableCell>}
      <TableCell>{row.dues_title}</TableCell>
      {!isMobile && <TableCell>{row.pledged_amount}</TableCell>}
      <TableCell>
        <TotalBalanceComponent totalPayment={row.pledged_amount} id={row.id} />
      </TableCell>
      <TableCell>
        <span
          className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${
            row.status && statusBgColor[parseInt(row.status) - 1]
          } ${row.status && statusTxtColor[parseInt(row.status) - 1]}`}
        >
          {row.status && paymentStatusOption[parseInt(row.status) - 1]}
        </span>
      </TableCell>
      {!isTablet && (
        <TableCell>
          {row.monthly_record_close_status ? (
            <FaLock className="text-red-600" />
          ) : (
            <FaLockOpen className="text-green-600" />
          )}
        </TableCell>
      )}
    </>
  );

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  return (
    <div className="px-4 py-6 mx-auto w-full max-w-7xl sm:px-6 lg:px-8">
      <div className="flex flex-col justify-between items-start mb-6 sm:flex-row sm:items-center">
        <h2 className="mb-4 text-2xl font-bold text-gray-900 sm:mb-0">
          Income Dues Management
        </h2>
        <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-2">
          {user?.is_a_trustee === true && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<FaPlus />}
              onClick={() => navigate("/dues-income/add")}
              className="bg-blue-600 hover:bg-blue-700"
              fullWidth={isMobile}
            >
              Add Dues
            </Button>
          )}
        </div>
      </div>

      <div className="mb-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Search by member name, category, or dues title..."
              InputProps={{
                startAdornment: (
                  <FaSearch className="mr-2 text-gray-400" size={16} />
                ),
                className: "bg-white rounded-lg shadow-sm",
              }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="p-4 mt-3 bg-white rounded-lg border border-gray-100 shadow-sm">
          {/* Month and Member Filter - Top Row */}
          <div className="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-2">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Filter by Month
              </label>
              <ReactSelect
                value={monthFilter}
                onChange={setMonthFilter}
                options={monthOptions}
                placeholder="Select month..."
                className="w-full"
                classNamePrefix="select"
                isClearable={true}
                isSearchable={true}
              />
            </div>
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Filter by Member
              </label>
              <Select
                value={memberFilter}
                onChange={(e) => setMemberFilter(e.target.value)}
                className="w-full"
                size="small"
                variant="outlined"
              >
                <MenuItem value="all">All Members</MenuItem>
                {members.map((member) => (
                  <MenuItem key={member} value={member}>
                    {member}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Start Date
              </label>
              <div className="w-full">
                <DatePicker
                  className="p-3 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                  placeholder="Select start date"
                  format="MM-DD-YYYY"
                  size="small"
                  style={{ width: "100%" }}
                  allowClear={true}
                />
              </div>
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                End Date
              </label>
              <div className="w-full">
                <DatePicker
                  className="p-3 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                  placeholder="Select end date"
                  format="MM-DD-YYYY"
                  size="small"
                  style={{ width: "100%" }}
                  allowClear={true}
                />
              </div>
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Status
              </label>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full"
                size="small"
                variant="outlined"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="1">Pending</MenuItem>
                <MenuItem value="2">Settled</MenuItem>
                <MenuItem value="3">Declined</MenuItem>
              </Select>
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-700">
                Category
              </label>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full"
                size="small"
                variant="outlined"
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <Button
              variant="outlined"
              size="small"
              onClick={clearFilters}
              className="text-sm font-medium text-gray-600 border-gray-300 hover:border-gray-400"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      <TableContainer component={Paper} className="rounded-lg shadow-lg">
        <Table>
          <TableHead>
            <TableRow className="bg-gray-50">
              <TableCell className="font-semibold">Action</TableCell>
              {!isMobile && (
                <TableCell className="font-semibold">Start Date</TableCell>
              )}
              <TableCell className="font-semibold">Member</TableCell>
              {!isTablet && (
                <TableCell className="font-semibold">Category</TableCell>
              )}
              <TableCell className="font-semibold">Dues Title</TableCell>
              {!isMobile && (
                <TableCell className="font-semibold">Pledged Amount</TableCell>
              )}
              <TableCell className="font-semibold">Balance Amount</TableCell>
              <TableCell className="font-semibold">Status</TableCell>
              {!isTablet && (
                <TableCell className="font-semibold">Record Status</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row) => (
              <TableRow key={row.id} className="hover:bg-gray-50">
                {renderTableCell(row)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination controls */}
      <div className="flex justify-between items-center p-4 mt-6 bg-white rounded-lg shadow-sm">
        <div className="flex gap-2 items-center">
          <span className="text-sm text-gray-600">Rows per page:</span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            className="px-2 py-1 text-sm rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {[5, 10, 25, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>
        <div className="flex gap-2 items-center">
          <Button
            variant="outlined"
            disabled={page === 0}
            onClick={() => setPage((prev) => prev - 1)}
            className="text-sm"
          >
            Previous
          </Button>
          <Button
            variant="outlined"
            disabled={paginatedData.length < rowsPerPage}
            onClick={() => setPage((prev) => prev + 1)}
            className="text-sm"
          >
            Next
          </Button>
        </div>
      </div>

      {/* Modal */}
      <ErrorBoundaryRoutes route="/dues-income/" page="ModalView">
        {showModal && (
          <Modal onClose={() => setShowModal(false)}>
            <ViewModal currentRecord={currentRecord} />
          </Modal>
        )}
      </ErrorBoundaryRoutes>
    </div>
  );
};

const TotalBalanceComponent = ({ id, totalPayment }) => {
  const [totalPaid, setTotalPaid] = useState();
  useEffect(() => {
    const getTotalPayment = (id) => {
      let userToken = localStorage.getItem("token");
      let urlLink = `${process.env.REACT_APP_API_URL}/incomedues/get-total-paid/${id}`;
      const config = {
        method: "GET",
        url: urlLink,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      };
      axios(config)
        .then(function (response) {
          console.log(response.data);
          setTotalPaid(response.data);
        })
        .catch(function (err) {
          console.log("error api call ", err);
        });
    };
    getTotalPayment(id);
  }, []);

  return <div>{totalPaid ? +totalPayment - +totalPaid : "N/A"}</div>;
};

export default ListIncomeDuesChild;
